import AntiSpamUtil from '../../../../util/AntiSpamUtil';
import UnifiedSignupUtil from './UnifiedSignupUtil';
import UnifiedSignupEmailType from './session-info/UnifiedSignupEmailType';

type EmailCheckResponse = {
  emailCheckCode: 200 | 204 | 401 | 422;
  emailType: UnifiedSignupEmailType;
  isDiscoEligible: boolean;
  errorReason?: string;
};

export type EmailStatus = 'PENDING_VERIFICATION' | 'VERIFIED' | 'INVALID' | 'DELIVERED';

/** Consolidates the different requested used for unified signup */
export default class UnifiedSignupAgent {
  public static async emailCheck({
    email,
  }: {
    email: string,
  }): Promise<EmailCheckResponse> {
    // 200 - happy path can create account
    // 204 - happy path cannot create account must see error
    // 401 - will either be redirected to edu upgrade or for pro make request to eloqua but NOT to invite, used the flask app (401 is returned as a 403 via the proxy)

    const csrfToken = AntiSpamUtil.getCsrfTokenOrNull()
      || await AntiSpamUtil.waitForCsrfToken({ isForce: true, timeoutMilliseconds: 1000 * 30 });

    if (!csrfToken) {
      return {
        emailCheckCode: 422,
        emailType: UnifiedSignupEmailType.nonGenEmail,
        isDiscoEligible: false,
        errorReason: 'CSRF token could not be obtained',
      };
    }

    const signedScore = AntiSpamUtil.getSignedScore();
    if (signedScore === '' || signedScore === 'unsigned') {
      // eslint-disable-next-line no-console
      console.error('Could not get signed score, reloading page to try again');
      const url = new URL(window.document.location.href);
      url.searchParams.set('no-signed-score', signedScore);
      url.searchParams.set('nonce', Math.random().toString(16).substring(0, 6));
      window.document.location = url.href;
    }

    // eslint-disable-next-line no-bitwise
    const nonce = Math.floor((Math.random() * (1 << 20))).toString(16);
    const checkPathname = '/api/fb/signupEmailCheck';
    const response = await fetch(`${checkPathname}?nonce=${nonce}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...await AntiSpamUtil.commonFetchHeaders(),
      },
      body: JSON.stringify({
        email,
        csrfToken,
        signedScore,
      }),
    });

    const result = await response.json();

    const { errorReason } = result;
    let { emailCheckCode } = result;
    emailCheckCode = UnifiedSignupUtil.getForcedEmailCheckCodeOrNull() || emailCheckCode;
    emailCheckCode = (emailCheckCode === 403) ? 401 : emailCheckCode;

    // eslint-disable-next-line no-console
    console.log('emailCheckCode', emailCheckCode);

    return {
      emailCheckCode,
      emailType: (result.isGeneric) ? UnifiedSignupEmailType.genEmail : UnifiedSignupEmailType.nonGenEmail,
      isDiscoEligible: result.isDiscoEligible,
      errorReason,
    };
  }

  public static async emailStatus({
    email,
  }: {
    email: string,
  }): Promise<{ emailStatusState: EmailStatus }> {
    /**
    public enum BTUserEmailState {
      // Email is pending verification
      PENDING_VERIFICATION,
      // Email is verified
      VERIFIED,
      // Email is invalid
      INVALID,
      // We have delivered the email
      DELIVERED
    }
    */

    // eslint-disable-next-line no-bitwise
    const nonce = Math.floor((Math.random() * (1 << 20))).toString(16);
    const response = await fetch(`/api/fb/signupEmailStatus?nonce=${nonce}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-www-referer': document.location.href,
      },
      body: JSON.stringify({
        email,
        csrfToken: AntiSpamUtil.getCsrfTokenOrNull(),
        signedScore: AntiSpamUtil.getSignedScore(),
      }),
    });

    const result = await response.json();

    return { emailStatusState: result.emailStatusState };
  }
}
